import axios from 'axios';
import { ref } from 'vue';

/**
 * Document service for managing document operations
 */
export const useDocumentService = () => {
  // State
  const isLoading = ref(false);
  const error = ref(null);
  const uploadProgress = ref(0);

  /**
   * Submits document and form data
   * @param {File} file - The file to upload
   * @param {Object} formData - Additional form data
   * @returns {Promise} Promise that resolves with the response
   */
  const submitDocument = async (file, formData) => {
    isLoading.value = true;
    error.value = null;
    uploadProgress.value = 0;
    
    try {
      // If using emailjs directly as in the original
      if (formData.useEmailJS) {
        return await submitViaEmailJS(file, formData);
      }
      
      // Otherwise use our API
      const data = new FormData();
      data.append('file', file);
      
      // Add all form data to the request
      Object.keys(formData).forEach(key => {
        if (key !== 'file' && key !== 'useEmailJS') {
          data.append(key, formData[key]);
        }
      });
      
      const response = await axios.post('/api/documents/upload', data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          uploadProgress.value = percentCompleted;
        }
      });
      
      return response.data;
    } catch (err) {
      console.error('Error uploading document:', err);
      error.value = 'Failed to upload document. Please try again.';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Submits document via EmailJS (legacy support)
   * @param {File} file - The file to upload 
   * @param {Object} formData - Additional form data
   * @returns {Promise} Promise that resolves with the response
   */
  const submitViaEmailJS = async (file, formData) => {
    // This method preserves compatibility with the original implementation
    try {
      // Convert file to base64 for EmailJS
      const reader = new FileReader();
      
      const filePromise = new Promise((resolve, reject) => {
        reader.onload = () => {
          const base64File = reader.result.split(',')[1]; // Remove content type part
          resolve(base64File);
        };
        reader.onerror = (error) => {
          reject(error);
        };
      });
      
      reader.readAsDataURL(file);
      const base64File = await filePromise;
      
      // Prepare EmailJS parameters
      const templateParams = {
        from_firstName: formData.firstName,
        from_lastName: formData.lastName,
        from_policyNumber: formData.policyNumber,
        from_email: formData.email,
        from_phoneNumber: formData.phoneNumber,
        from_message: formData.message,
        attachment: base64File,
      };
      
      // Import EmailJS dynamically to avoid loading it unnecessarily
      const emailjs = await import('@emailjs/browser');
      
      // Send email with attachment
      const response = await emailjs.send(
        'service_8sokp7z',
        'template_z0qqwz8',
        templateParams,
        '1uqLc0qiNpveMBpl-',
      );
      
      return response;
    } catch (err) {
      console.error('Error with EmailJS:', err);
      error.value = 'Failed to send document via email. Please try again.';
      throw err;
    }
  };
  
  /**
   * Get available document templates
   * @returns {Array} List of available documents
   */
  const getAvailableDocuments = () => {
    // Return static list of documents for now
    // In a real implementation, this might come from an API
    return [
      {
        name: 'Flood Affirmation Form',
        url: '/pdfs/Flood_Affirmation_Form.pdf',
        category: 'claims'
      },
      {
        name: 'Policyholder Affirmation Form',
        url: '/pdfs/Policyholder_Affirmation_Regarding_Primary_Residence.pdf',
        category: 'policy'
      }
    ];
  };
  
  return {
    isLoading,
    error,
    uploadProgress,
    submitDocument,
    getAvailableDocuments
  };
}; 